import { currentLocaleConfig } from '@/lib/i18n/localeSpecific';
import { cn } from '@/lib/utils';
import { getWrapperMaxWidth } from '@/cms/utils';
import { Text } from '@/decodo/components/atoms/Text';
import FooterCategories from './FooterCategories';
import { FooterStrapiProps } from './FooterStrapi';
import ImageStrapi from '@/decodo/components/atoms/Image/ImageStrapi';
import { Divider } from '@/decodo/components/atoms/Divider';
import { ServiceStatus } from '@/decodo/components/atoms/Instatus';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';
import FooterContacts from './FooterContacts';

export const mutualLinkStyle =
  'text-body-m font-mono text-gray-950 dark:text-base-dark-white hover:text-brand-500 dark:hover:text-brand-500';

export const Footer = (props: FooterStrapiProps['footer']) => {
  const currentYear = new Date().getFullYear();

  if (!props) {
    return null;
  }

  const hasFooterLinks = !!props.contacts?.length || !!props.categories?.length;

  return (
    <>
      <footer
        className={cn(
          'text-base-white bg-gray-25 dark:bg-gray-dark-975 flex justify-center py-8 md:py-20'
        )}
        data-tracker-section='footer'
        data-tracker-component='footer'
      >
        <div className={cn(getWrapperMaxWidth('mid'), 'w-full px-5')}>
          <div className={cn(hasFooterLinks && 'mb-8 md:mb-12')}>
            <ImageStrapi
              inner={{
                classNames: {
                  image: 'h-5',
                },
              }}
              block={props.logo ?? {}}
            />
          </div>
          <div
            className='flex flex-col flex-nowrap gap-10 md:flex-row md:gap-20 '
            data-tracker-section={SECTION_NAMES.FOOTER}
          >
            <div
              className={cn(
                'grid h-fit grid-rows-[auto_auto] gap-8 md:gap-[60px]',
                (props.contacts?.length || 0) > 2
                  ? 'grid-cols-1 md:grid-cols-[200px_200px]'
                  : 'grid-cols-1 md:grid-cols-[200px]'
              )}
            >
              {!!props.contacts?.length && (
                <FooterContacts contacts={props.contacts} />
              )}
              <ServiceStatus
                size='sm'
                classNames={{
                  // force a slightly wider button width to fit the
                  // text that's there 99% of the time - "All systems operational"
                  button: cn(
                    'w-[200px] text-gray-950 dark:text-base-dark-white',
                    props.contacts && !props.contacts.length && 'mt-6'
                  ),
                }}
              />
            </div>
            {!!props.categories?.length && (
              <div className='hidden md:block'>
                <Divider
                  showOnDark
                  alignment='vertical'
                  classNames={{
                    root: 'dark:border-gray-dark-700 border-gray-200',
                  }}
                />
              </div>
            )}
            {!!props.categories?.length && (
              <FooterCategories categories={props.categories} />
            )}
          </div>
        </div>
      </footer>
      <div className='dark:bg-gray-dark-900 bg-gray-50 py-3 text-center'>
        <Text
          variant='body-l'
          classNames={{
            wrapper: 'text-gray-500 dark:text-gray-dark-400',
          }}
        >
          © 2018-{currentYear} {currentLocaleConfig().footerAllRightsReserved}
        </Text>
      </div>
    </>
  );
};
